import { UIEvent } from 'react'
import { DatePicker, Form, Input, Radio, Select } from 'antd'
import type { RangePickerProps } from 'antd/es/date-picker'
import dayjs from 'dayjs'
import { FormikHelpers, FormikState } from 'formik'
import { NumericFormat } from 'react-number-format'
import Card from 'components/card/Card'
import CardHeaderForm from 'components/card/CardHeaderForm'
import RequiredMark from 'components/form/RequiredMark'
import ErrorHintForm from 'components/errors/ErrorHintForm'
import FormProductPriceValue from 'constants/interfaceFormik/productStockPrice'
import { AdminsGetProductListResponse } from 'generated/api'
import { classNameInput } from 'helpers/formik/classNameForm'

interface BasicInformationProps {
  state: FormikState<FormProductPriceValue>
  helpers: FormikHelpers<FormProductPriceValue>
  products: AdminsGetProductListResponse[]
  onDropDownScrollFetch: (event: UIEvent<HTMLDivElement>) => void
  onChangeProductNameSearch: (value: string) => void
}

export default function BasicInformation(props: BasicInformationProps) {
  const {
    products,
    onDropDownScrollFetch,
    onChangeProductNameSearch,
    helpers: { setFieldValue, setFieldError },
    state,
    state: { errors, touched, values },
  } = props

  const disabledDateStart: RangePickerProps['disabledDate'] = current => {
    return current && current.valueOf() <= dayjs().subtract(1, 'day').endOf('day').valueOf()
  }

  const disabledDateEnd: RangePickerProps['disabledDate'] = current => {
    const compare = values.startDateUtc0
      ? dayjs(values.startDateUtc0).subtract(1, 'day').endOf('day').valueOf()
      : dayjs().subtract(1, 'day').endOf('day').valueOf()
    return current && current.valueOf() <= compare
  }

  const handleDropdownScroll = (event: UIEvent<HTMLDivElement>) => {
    onDropDownScrollFetch(event)
  }

  const handleChangeStartDate = async (_date: dayjs.Dayjs | null, dateString: string) => {
    const utcDate = dayjs(`${dateString} 00:00:00`, 'DD/MM/YYYY HH:mm:ss').toDate().valueOf()
    await setFieldValue('startDateUtc0', utcDate || 0)
    await setFieldValue('endDateUtc0', 0)
    if (utcDate) setFieldError('startDateUtc0', '')
  }

  const handleChangeEndDate = async (_date: dayjs.Dayjs | null, dateString: string) => {
    const utcDate = dayjs(`${dateString} 23:59:59`, 'DD/MM/YYYY HH:mm:ss').toDate().valueOf()
    await setFieldValue('endDateUtc0', utcDate || 0)
    if (utcDate) setFieldError('endDateUtc0', '')
  }

  const classNameInputStockAmount = classNameInput('stockAmount', state)
  const classNameInputPreparationDuration = classNameInput('preparationDuration', state)
  const classNameInputStartDateUtc0 = classNameInput('startDateUtc0', state)
  const classNameInputEndDateUtc0 = classNameInput('endDateUtc0', state)

  return (
    <Card className="rounded-xl py-6 px-0" dataTestId="form_basicInformation">
      <CardHeaderForm title="1. Informasi Dasar" />
      <Card.Body className="mt-0 px-6">
        <Form
          className="flex flex-col gap-3 xl:gap-9"
          layout="horizontal"
          preserve={false}
          requiredMark={RequiredMark}
          labelCol={{ prefixCls: 'xl:w-3/12' }}
          labelAlign="left"
          labelWrap
          colon={false}
          wrapperCol={{ prefixCls: 'xl:w-9/12' }}
          style={{ maxWidth: '100%' }}
        >
          <div>
            <Form.Item
              name="productid"
              label={<div className="text-base font-medium text-[#323539] m-0 inline">Produk</div>}
              className="mb-0"
              required
              valuePropName="productid"
              wrapperCol={{ prefixCls: 'xl:flex xl:gap-6' }}
            >
              <div>
                <Select
                  id="productid"
                  showSearch
                  data-testid="select_productId"
                  className="selector h-full mb-1 xl:w-2/5 w-full"
                  placeholder="Pilih Produk"
                  status={errors.productId && touched.productId ? 'error' : ''}
                  virtual={false}
                  filterOption={false}
                  onSearch={value => onChangeProductNameSearch(value)}
                  onPopupScroll={handleDropdownScroll}
                  onChange={value => {
                    const product = products.find(product => product.id === value)
                    setFieldValue('productId', product?.id)
                  }}
                  disabled={!!values.productName}
                  value={values.productName ? values.productName : values.productId || null}
                >
                  {products.map(product => {
                    return (
                      <Select.Option
                        data-testid={`select_product_id_${product.id}`}
                        key={product.id}
                        value={product.id}
                      >
                        {product.name}
                      </Select.Option>
                    )
                  })}
                </Select>

                {errors.productId && touched.productId ? (
                  <ErrorHintForm
                    errorMessage={errors.productId}
                    dataTestId="error-message-productid"
                  />
                ) : null}
              </div>
            </Form.Item>
          </div>
          <div>
            <Form.Item
              name="stockamount"
              label={
                <div className="text-base font-medium text-[#323539] m-0 inline">
                  Stok Barang Saat Ini
                </div>
              }
              className="mb-0"
              required
              valuePropName="stockamount"
              wrapperCol={{ prefixCls: 'xl:flex xl:gap-6' }}
            >
              <NumericFormat
                id="stockamount"
                allowNegative={false}
                className={`xl:w-2/5 w-full mb-1 ${classNameInputStockAmount}`}
                customInput={Input}
                data-testid="input_stockAmount"
                decimalScale={0}
                decimalSeparator=","
                onValueChange={val => setFieldValue('stockAmount', val.floatValue ?? '')}
                placeholder="Contoh: 7"
                status={errors.stockAmount && touched.stockAmount ? 'error' : ''}
                thousandSeparator="."
                value={values.stockAmount}
              />
            </Form.Item>
            {errors.stockAmount && touched.stockAmount ? (
              <div className="xl:translate-x-1/4">
                <ErrorHintForm
                  errorMessage={errors.stockAmount}
                  dataTestId="error-message-stockamount"
                />
              </div>
            ) : null}
          </div>
          <div>
            <Form.Item
              name="preparedduration"
              label={
                <div className="text-base font-medium text-[#323539] m-0 inline">
                  Waktu Proses Barang (SLA)
                </div>
              }
              className="mb-0"
              required
              valuePropName="preparedduration"
              wrapperCol={{ prefixCls: 'xl:flex xl:gap-6' }}
            >
              <NumericFormat
                prefix="Hari "
                id="preparedduration"
                allowNegative={false}
                className={`xl:w-2/5 w-full mb-1 ${classNameInputPreparationDuration}`}
                customInput={Input}
                data-testid="input_preparationDuration"
                decimalScale={0}
                decimalSeparator=","
                onValueChange={val => setFieldValue('preparationDuration', val.floatValue ?? '')}
                placeholder="Hari 0"
                status={errors.preparationDuration && touched.preparationDuration ? 'error' : ''}
                thousandSeparator="."
                value={values.preparationDuration}
              />
            </Form.Item>
            {errors.preparationDuration && touched.preparationDuration ? (
              <div className="xl:translate-x-1/4">
                <ErrorHintForm
                  errorMessage={errors.preparationDuration}
                  dataTestId="error-message-preparationduration"
                />
              </div>
            ) : null}
          </div>
          <Form.Item
            label={
              <div className="text-base font-medium text-[#323539] m-0 inline">Periode Aktif</div>
            }
            className="mb-0"
            wrapperCol={{ prefixCls: 'xl:flex xl:gap-6' }}
          >
            <div className="xl:flex xl:flex-row xl:gap-2 xl:w-10/12">
              <div className="xl:w-1/2">
                <Form.Item
                  name="startdateutc0"
                  label={
                    <div className="text-xs font-bold text-[#323539] m-0 inline">Tanggal Mulai</div>
                  }
                  className="mb-0 vertical-form-item"
                  required
                  valuePropName="startdateutc0"
                >
                  <DatePicker
                    id="startdateutc0"
                    data-testid="input_startDateUtc0"
                    value={
                      values.startDateUtc0 ? dayjs.utc(values.startDateUtc0).local() : undefined
                    }
                    className={`w-full ${classNameInputStartDateUtc0}`}
                    format="DD/MM/YYYY"
                    disabledDate={disabledDateStart}
                    status={errors.startDateUtc0 && touched.startDateUtc0 ? 'error' : ''}
                    onChange={(date, dateString) =>
                      handleChangeStartDate(date, dateString as string)
                    }
                  />
                </Form.Item>
                {errors.startDateUtc0 && touched.startDateUtc0 ? (
                  <ErrorHintForm
                    errorMessage={errors.startDateUtc0}
                    dataTestId="error-message-startdateutc0"
                  />
                ) : null}
              </div>
              <div className="xl:w-1/2">
                <Form.Item
                  name="enddateutc0"
                  label={
                    <div className="text-xs font-bold text-[#323539] m-0 inline">
                      Tanggal Selesai
                    </div>
                  }
                  className="mb-0 vertical-form-item"
                  required
                  valuePropName="enddateutc0"
                >
                  <DatePicker
                    id="enddateutc0"
                    data-testid="input_endDateUtc0"
                    value={values.endDateUtc0 ? dayjs.utc(values.endDateUtc0).local() : undefined}
                    className={`w-full ${classNameInputEndDateUtc0}`}
                    format="DD/MM/YYYY"
                    disabledDate={disabledDateEnd}
                    status={errors.endDateUtc0 && touched.endDateUtc0 ? 'error' : ''}
                    onChange={(date, dateString) => handleChangeEndDate(date, dateString as string)}
                  />
                </Form.Item>
                {errors.endDateUtc0 && touched.endDateUtc0 ? (
                  <ErrorHintForm
                    errorMessage={errors.endDateUtc0}
                    dataTestId="error-message-enddateutc0"
                  />
                ) : null}
              </div>
            </div>
          </Form.Item>
          <Form.Item
            label={
              <div className="text-base font-medium text-[#323539] m-0 inline">Product Inquiry</div>
            }
            className="mb-0"
            required
            valuePropName="isinquiryallowed"
            wrapperCol={{ prefixCls: 'xl:flex xl:gap-6' }}
          >
            <Radio.Group
              data-testid="radio_group_isInquiryAllowed"
              onChange={e => setFieldValue('isInquiryAllowed', e.target.value)}
              value={values.isInquiryAllowed}
            >
              <Radio value={true} data-testid="radio_isInquirtAllowed_active">
                Aktif
              </Radio>
              <Radio value={false}>Tidak Aktif</Radio>
            </Radio.Group>
          </Form.Item>
        </Form>
      </Card.Body>
    </Card>
  )
}
